/**
 * Countdown Timer Class
 * Clean, efficient countdown functionality
 */
class CountdownTimer {
  constructor(endDate, containerId = 'countdown-timer') {
    this.endDate = new Date(endDate);
    this.containerId = containerId;
    
    // Validate date
    if (isNaN(this.endDate.getTime())) {
      console.error('Invalid countdown date:', endDate);
      return;
    }
    
    // Get DOM elements
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error('Countdown container not found:', containerId);
      return;
    }
    
    // Get number elements (support both regular and global versions)
    const suffix = containerId.includes('global') ? '-global' : '';
    this.elements = {
      days: document.getElementById('days' + suffix),
      hours: document.getElementById('hours' + suffix),
      minutes: document.getElementById('minutes' + suffix),
      seconds: document.getElementById('seconds' + suffix)
    };
    
    // Validate elements exist
    const missing = Object.keys(this.elements).filter(key => !this.elements[key]);
    if (missing.length > 0) {
      console.error('Missing countdown elements:', missing);
      return;
    }
    
    this.start();
  }
  
  start() {
    // Update immediately
    this.update();
    
    // Update every second
    this.interval = setInterval(() => this.update(), 1000);
  }
  
  update() {
    const now = new Date().getTime();
    const distance = this.endDate.getTime() - now;
    
    // Check if countdown finished
    if (distance < 0) {
      this.finish();
      return;
    }
    
    // Calculate time units
    const time = {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    };
    
    // Update display
    Object.keys(time).forEach(unit => {
      if (this.elements[unit]) {
        this.updateElement(this.elements[unit], time[unit]);
      }
    });
    
    // Add urgency effect when less than 1 hour
    if (distance < 3600000) { // 1 hour in milliseconds
      this.container.classList.add('countdown-urgent');
    } else {
      this.container.classList.remove('countdown-urgent');
    }
  }
  
  updateElement(element, value) {
    const formatted = value.toString().padStart(2, '0');
    
    if (element.textContent !== formatted) {
      element.textContent = formatted;
      
      // Add update animation
      element.classList.add('updated');
      setTimeout(() => element.classList.remove('updated'), 300);
    }
  }
  
  finish() {
    // Clear interval
    if (this.interval) {
      clearInterval(this.interval);
    }
    
    // Hide countdown bar
    const bar = document.querySelector('.countdown-announcement-bar');
    if (bar) {
      bar.style.display = 'none';
    }
    
    // Dispatch event
    document.dispatchEvent(new CustomEvent('countdownEnded', {
      detail: { containerId: this.containerId }
    }));
  }
  
  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

/**
 * Initialize countdown timers
 */
function initCountdowns() {
  // Regular countdown
  const element = document.getElementById('countdown-timer');
  if (element) {
    const endDate = element.dataset.endDate || window.countdownEndDate;
    if (endDate) {
      window.countdownTimer = new CountdownTimer(endDate, 'countdown-timer');
    }
  }
  
  // Global countdown
  const globalElement = document.getElementById('countdown-timer-global');
  if (globalElement) {
    const endDate = globalElement.dataset.endDate || window.countdownEndDate;
    if (endDate) {
      window.globalCountdownTimer = new CountdownTimer(endDate, 'countdown-timer-global');
    }
  }
}

/**
 * Event listeners
 */
document.addEventListener('DOMContentLoaded', initCountdowns);

// Backup initialization
if (document.readyState !== 'loading') {
  setTimeout(initCountdowns, 100);
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.countdownTimer) window.countdownTimer.destroy();
  if (window.globalCountdownTimer) window.globalCountdownTimer.destroy();
});